import React, { useEffect, useState, useRef } from 'react';
import { faMicrophone } from '@fortawesome/pro-solid-svg-icons/faMicrophone';
import { faMicrophoneSlash } from '@fortawesome/pro-solid-svg-icons/faMicrophoneSlash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSharedTranslation } from '../hooks/useSharedTranslation';

interface SpeechRecognitionProps {
  isLoading: boolean;
  setInput: (value: string | ((prev: string) => string)) => void;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  error: string;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: {
    isFinal: boolean;
    [index: number]: {
      transcript: string;
    };
  };
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionEvent) => void;
  onend: () => void;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type SpeechRecognitionWindow = Window & {
  SpeechRecognition?: { new (): SpeechRecognitionInstance };
  webkitSpeechRecognition?: { new (): SpeechRecognitionInstance };
};

const getSpeechRecognition = (): { new (): SpeechRecognitionInstance } | undefined => {
  if (typeof window === 'undefined') return undefined;

  const currentWindow = window as SpeechRecognitionWindow;

  return currentWindow.SpeechRecognition || currentWindow.webkitSpeechRecognition;
};

export const SpeechRecognition = ({ isLoading, setInput }: SpeechRecognitionProps): JSX.Element | null => {
  const { t } = useSharedTranslation();
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  useEffect(() => {
    const SpeechRecognitionAPI = getSpeechRecognition();

    if (!SpeechRecognitionAPI) {
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent): void => {
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];

        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        }
      }

      if (finalTranscript) {
        setInput(prev => prev + finalTranscript);
      }
    };

    recognition.onerror = (event: SpeechRecognitionEvent): void => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = (): void => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return (): void => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [setInput]);

  if (!getSpeechRecognition()) {
    return null;
  }

  const toggleSpeechRecognition = (): void => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  return (
    <button
      type='button'
      onClick={toggleSpeechRecognition}
      disabled={isLoading}
      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
        isListening
          ? 'bg-red-500 text-white hover:bg-red-600 active:bg-red-600'
          : 'bg-emerald-700 text-white hover:bg-emerald-500 active:bg-emerald-500'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      aria-label={isListening ? t('components.speechRecognition.stopRecording') : t('components.speechRecognition.startVoiceInput')}
      title={isListening ? t('components.speechRecognition.stopRecording') : t('components.speechRecognition.startVoiceInput')}
    >
      <FontAwesomeIcon icon={isListening ? faMicrophoneSlash : faMicrophone} className='w-4 h-4' />
    </button>
  );
};
